import { useResource } from 'json-api-hooks';
import { Feature, Map as OLMap } from 'ol';
import { Coordinate } from 'ol/coordinate';
import { Extent } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import React from 'react';

import { FOCUS_ZOOM } from '../components/Map/hooks/useMap';
import { ViewProps } from '../components/Map/lib/types';

const outerBoundaryStyle = new Style({
  stroke: new Stroke({ color: '#323232', width: 5 }),
});

// Geometries from the server are in EPSG:3857
const geoJsonFormat = new GeoJSON({ dataProjection: 'EPSG:3857', featureProjection: 'EPSG:3857' });

interface UseSelectedPostalCode {
  map?: OLMap;
  selectedPostalCode?: number;
  setView: (view: ViewProps) => void;
  setOverlayPosition: (position: Coordinate) => void;
  view: ViewProps;
}

const navigateTo = (
  extent: Extent,
  view: ViewProps,
  setOverlayPosition: (c: Coordinate) => void,
  setView: (v: ViewProps) => void,
) => {
  /* eslint-disable @typescript-eslint/no-magic-numbers */
  const center = [(extent[0] + extent[2]) / 2, extent[1]] as Coordinate;
  /* eslint-enable @typescript-eslint/no-magic-numbers */
  setOverlayPosition(center);
  setView({ center, zoom: Math.max(view?.zoom || 0, FOCUS_ZOOM) });
};

const useSelectedPostalCode = ({
  map,
  selectedPostalCode,
  setOverlayPosition,
  setView,
  view,
}: UseSelectedPostalCode): Feature | undefined => {
  const [postalCode] = useResource({
    type: 'postal_codes',
    id: selectedPostalCode ? String(selectedPostalCode) : undefined,
  });
  const [boundaryFeature, setBoundaryFeature] = React.useState<Feature | undefined>(undefined);

  React.useEffect(() => {
    if (!postalCode || !map) {
      setBoundaryFeature(undefined);

      return;
    }

    const feature = geoJsonFormat.readFeature(postalCode.attributes.geometry) as Feature;
    feature.setStyle(outerBoundaryStyle);
    feature.set('hoverStyle', () => outerBoundaryStyle);
    setBoundaryFeature(feature);
    navigateTo(postalCode.attributes.extent, view, setOverlayPosition, setView);
  }, [map, postalCode]);

  return boundaryFeature;
};

export default useSelectedPostalCode;
